import { errorToast } from "@/components/ui/toast.tsx";
import type {
  MeetingPreferenceCreateReq,
  MeetingPreferenceListResp,
  MeetingPreferenceUpdateReq,
} from "@/types/meetingPreferences.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createMeetingPreference,
  deleteMeetingPreference,
  getMeetingPreferences,
  updateMeetingPreference,
} from "./meetingPreferencesApi.ts";

const QUERY_KEY = ["meetingPreferences"] as const;

/** Extract the `reason` field a 409 surfaces from the meetingPreferences handler. */
function errorReason(err: unknown): string | undefined {
  return (err as { response?: { data?: { reason?: string } } })?.response?.data?.reason;
}

export const useMeetingPreferences = () => {
  return useQuery<MeetingPreferenceListResp>({
    queryKey: QUERY_KEY,
    queryFn: getMeetingPreferences,
  });
};

export const useCreateMeetingPreference = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: MeetingPreferenceCreateReq) => createMeetingPreference(data),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => {
      errorToast(
        errorReason(err) === "duplicateMeetingType"
          ? "A meeting preference with that name already exists"
          : "Failed to create meeting preference",
      );
    },
  });
};

export const useUpdateMeetingPreference = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: MeetingPreferenceUpdateReq }) =>
      updateMeetingPreference(id, data),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: (err) => {
      errorToast(
        errorReason(err) === "duplicateMeetingType"
          ? "A meeting preference with that name already exists"
          : "Failed to update meeting preference",
      );
    },
  });
};

export const useDeleteMeetingPreference = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => deleteMeetingPreference(id),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: QUERY_KEY });
    },
    onError: () => {
      errorToast("Failed to delete meeting preference");
    },
  });
};
